import { useState } from 'react';
import api from '../lib/api';
import { Section, Spinner, ErrorNote, Empty, useAsync } from './ui';

// Publisher panel to invite members to a course (design/018 Phase 3). Invitees
// get an email with an accept link + 6-digit code; accepting (on the web)
// creates a comped enrollment. Lists pending invites with resend/revoke.
export default function CourseInvitePanel({ courseId, priced = false }) {
  // V61 — paid program: the inviter can let this person in free.
  const [free, setFree] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [sending, setSending] = useState(false);
  const { loading, error, data, reload } = useAsync(
    () => api.adminListCourseInvites(courseId).then((x) => (Array.isArray(x) ? x : [])).catch(() => []),
    [courseId],
  );

  const send = async () => {
    if (!email.trim()) { alert('Email is required.'); return; }
    setSending(true);
    try {
      await api.adminInviteToCourse(courseId, { email: email.trim(), name: name.trim() || null, freeAccess: priced && free });
      setEmail(''); setName(''); setFree(false);
      reload();
    } catch (e) { alert(e.message); }
    finally { setSending(false); }
  };

  const resend = async (inv) => {
    try { await api.adminResendCourseInvite(courseId, inv.id); alert(`Resent to ${inv.inviteeEmail}.`); }
    catch (e) { alert(e.message); }
  };
  const revoke = async (inv) => {
    if (!window.confirm(`Revoke invite to ${inv.inviteeEmail}?`)) return;
    try { await api.adminRevokeCourseInvite(courseId, inv.id); reload(); } catch (e) { alert(e.message); }
  };

  return (
    <Section title="Invite members">
      <p style={{ margin: '0 0 12px', color: 'var(--muted)', fontSize: '0.875rem' }}>
        Invite someone by email. They'll get a link to accept and enroll (enrollment
        is handled on the web).
      </p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div className="form-field" style={{ flex: 2, minWidth: 200, marginBottom: 0 }}>
          <label>Email <span className="req">*</span></label>
          <input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="person@example.com" />
        </div>
        <div className="form-field" style={{ flex: 1, minWidth: 140, marginBottom: 0 }}>
          <label>Name (optional)</label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <button className="btn btn-primary" disabled={sending} onClick={send}>{sending ? 'Sending…' : 'Send invite'}</button>
      </div>
      {priced && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, cursor: 'pointer', fontSize: '0.9rem' }}>
          <input type="checkbox" checked={free} onChange={(e) => setFree(e.target.checked)} />
          <span><b>Free for this person</b> — they won't be charged. Leave unticked and they pay the price when they accept.</span>
        </label>
      )}

      <div style={{ marginTop: 18, borderTop: '1px solid var(--border, #e5e7eb)', paddingTop: 14 }}>
        <div className="section-title" style={{ marginBottom: 10 }}>Pending invites</div>
        {loading ? <Spinner /> : error ? <ErrorNote error={error} onRetry={reload} /> : (
          (!data || data.length === 0) ? <Empty>No pending invites.</Empty> : (
            data.map((inv) => (
              <div key={inv.id} className="member-row">
                <div style={{ flex: 1 }}>
                  <div className="nm">{inv.inviteeName || inv.inviteeEmail}<InvitePriceTag inv={inv} /></div>
                  <div className="em">{inv.inviteeName ? inv.inviteeEmail : `code ${inv.shortCode}`}</div>
                </div>
                <button className="btn btn-sm" onClick={() => resend(inv)}>Resend</button>
                <button className="btn btn-sm btn-danger" onClick={() => revoke(inv)}>Revoke</button>
              </div>
            ))
          )
        )}
      </div>
    </Section>
  );
}

/** "Free (normally $22.00)" for a free invite to a paid program, "Pays $22.00" otherwise. */
function InvitePriceTag({ inv }) {
  const price = inv.priceCents || 0;
  if (price <= 0) return null;
  const money = `$${(price / 100).toFixed(2)}`;
  return inv.freeAccess
    ? <span className="pill" style={{ marginLeft: 6, background: '#dcfce7', color: '#166534' }}>Free (normally {money})</span>
    : <span className="pill" style={{ marginLeft: 6, background: '#f3f4f6', color: 'var(--muted)' }}>Pays {money}</span>;
}
